<template>
    <div class="free-term" :class="{'highlighted': showChoosingPrompt || showChangingPrompt}">
        <div class="column">
            <div class="icon-container">
                <img src="../../images/calendar-blue.png">
            </div>
            <div class="content-container">
                <div class="date">{{ freeTerm.date | dateFilter }}</div>
                <div class="time">{{ freeTerm.timeFrom }} - {{ freeTerm.timeTo }}</div>
            </div>
        </div>

        <div class="column">
            <div class="icon-container">
                <img src="../../images/doctor-blue.png">
            </div>
            <div class="content-container">
                <div class="doctor">{{ freeTerm.doctor_name }}</div>
                <div class="specialty">{{ specialtyName }}</div>
            </div>
        </div>

        <div class="column">
            <div class="icon-container">
                <img src="../../images/place-blue.png">
            </div>
            <div class="content-container">
                <div class="department-name">{{ department.name }}</div>
                <div class="department-address">
                    {{ department.city }}, ul. {{ department.street }} {{ department.house_number }}
                    <template v-if="department.flat_number"> lok. {{ department.flat_number }}</template>
                </div>
                <div class="department-telephone">Tel: {{ department.telephone }}</div>
            </div>
        </div>

        <div class="buttons" v-if="isNew">
            <button class="btn-outline-blue btn-choose" v-if="!showChoosingPrompt" @click="toggleShowChoosingPrompt"
                    :class="{'btn-outline-grey' : isDisabled, 'not-allowed': isDisabled}"
                    :disabled="isDisabled">WYBIERZ</button>
            <template v-else>
                <div class="prompt">Czy na pewno chcesz wybrać wizytę?</div>
                <div class="confirmation-buttons">
                    <button class="btn-outline-blue" @click="chooseFreeTerm">Tak</button>
                    <button class="btn-outline-orange" @click="toggleShowChoosingPrompt">Nie</button>
                </div>
            </template>
        </div>

        <div class="buttons" v-if="isChosen">
            <button class="btn-outline-blue btn-choose" v-if="!showChangingPrompt"
                    @click="toggleShowChangingPrompt">ZMIEŃ</button>
            <template v-else>
                <div class="prompt">Czy na pewno chcesz zmienić wizytę?</div>
                <div class="confirmation-buttons">
                    <button class="btn-outline-blue" @click="changeFreeTerm">Tak</button>
                    <button class="btn-outline-orange" @click="toggleShowChangingPrompt">Nie</button>
                </div>
            </template>
        </div>

        <div class="row" v-show="showChoosingPrompt || isChosen">
            <label class="note-label">Dodaj notatkę:</label>
            <input class="patient-note" v-model="freeTerm.patient_note" />
        </div>
    </div>
</template>

<style lang="scss" scoped>
@import "../../css/constants.scss";

$free-term-color: #4d84b9;
$free-term-bg-color: #f6f9fb;
$min-column-width: 200px;
$emphasis-font-size: 17.5px;
$input-border-color: #afafaf;

.free-term {
    display: flex;
    flex-flow: row wrap;
    border: 1px solid $free-term-color;
    background: $free-term-bg-color;
    padding-top: 0.8em;
    border-radius: 15px;
    transition: all 0.3s ease 0s;
    margin-bottom: 1em;
    font-weight: 300;
    max-width: $free-term-max-width;
    width: 100%;
    min-height: 110px;

    .row {
        padding: 0 20px 10px;
        width: 100%;

        display: block;

        .patient-note {
            padding: 5px 10px;
            width: calc(100% - 110px - 2px - 40px);
            border: 1px solid $input-border-color;
            border-radius: 10px;
        }

        .note-label {
            display: inline-block;
            font-weight: bold;
            width: 110px;
        }
    }

    .column {
        display: flex;
        flex-grow: 1;
        flex-basis: 0;
        margin-left: 1em;
        margin-right: 1em;
        min-width: $min-column-width;
        padding-bottom: 0.5em;

        .icon-container {
            text-align: center;
            align-self: center;
            min-width: 40px;
        }
        .content-container {
            align-self: center;
            padding-left: 1em;

            >div {
                padding-bottom: 0.2em;
            }
            .date,
            .doctor,
            .department-name {
                font-weight: 500;
                font-size: $emphasis-font-size;
            }
            .time {
                font-size: $emphasis-font-size;
            }
        }
    }
    .buttons {
        flex-grow: 1;
        flex-basis: 0;
        text-align: center;
        align-self: center;
        align-items: center;
        padding-bottom: 0.5em;
        padding-left: 1em;
        padding-right: 1em;
        display: flex;
        flex-flow: column;
        min-width: $min-column-width;

        .prompt {
            margin-bottom: 0.5em;
            font-weight: 400;
        }
        .btn-choose {
            min-width: 140px;
            margin: auto;
            align-self: center;
        }
        .confirmation-buttons {
            margin-left: 0.5em;
        }
    }
    &.highlighted {
        outline: none;
        -webkit-box-shadow: 0 0 5px 0 #9ecaed;
        -moz-box-shadow: 0 0 5px 0 #9ecaed;
        box-shadow: 0 0 5px 0 #9ecaed;
        border-collapse: separate;
    }
    @media all and (min-width: 970px) {
        .buttons {
            flex-grow: 0;
            flex-basis: $min-column-width;

            .confirmation-buttons {
                margin: auto;
            }
        }
    }
}
</style>

<script>
import ApiMixin from '../mixins/ApiMixin.js'

import {
    ANY_SPECIALTY_PLACEHOLDER,
    FREE_TERM_TYPES as TYPES,
} from '../constants.js'

export default {
    name: 'FreeTerm',
    mixins: [
        ApiMixin,
    ],
    props: {
        // Single freeTerm object.
        freeTerm: {
            type: Object,
            required: true,
        },
        // Type of the the current freeTerm slot (property of FREE_TERM_TYPES).
        type: {
            type: String,
            required: true,
            validator: val => Object.values(TYPES).includes(val),
        },
    },
    data() {
        return {
            department: this.$store.departmentsMap[this.freeTerm.department],
            showChoosingPrompt: false, // Prompt asking whether user really wants to choose clicked term.
            showChangingPrompt: false, // Prompt asking whether user really wants to change chosen term.

            TYPES: TYPES,
        }
    },
    computed: {
        specialtyName() {
            if (!this.freeTerm.specialty || this.freeTerm.specialty.name === ANY_SPECIALTY_PLACEHOLDER) {return '';}
            return this.freeTerm.specialty.name;
        },
        isNew() {
            return this.type === TYPES.NEW;
        },
        isChosen() {
            return this.type === TYPES.CHOSEN;
        },
        isDisabled() {
            // Disable choosing this free term unless there is no term chosen yet.
            return this.$store['FreeTerm-hasSomeFreeTermBeenChosen'];
        },
        redirectionAfterChoosingFreeTerm() {
            return this.$store.isPatientLoggedIn
                ? {name: 'free-term-confirmation', params: {freeTerm: this.freeTerm}}
                : {name: 'login'};
        },
    },
    methods: {
        chooseFreeTerm() {
            this.$bus.$emit('$store:setFreeTerm-chosenFreeTerm', this.freeTerm);
            this.$router.push(this.redirectionAfterChoosingFreeTerm);
        },
        toggleShowChoosingPrompt() {
            this.showChoosingPrompt = !this.showChoosingPrompt;
            this.$bus.$emit('$store:setFreeTerm-hasSomeFreeTermBeenChosen', this.showChoosingPrompt);
        },
        changeFreeTerm() {
            // Reset globally set FreeTerm-chosenFreeTerm variable from the store if
            // user wants to change it and redirect to the main visit planner view.
            this.$bus.$emit('$store:setFreeTerm-chosenFreeTerm', null);
            this.$router.push({name: 'index'});
        },
        toggleShowChangingPrompt() {
            this.showChangingPrompt = !this.showChangingPrompt;
        },
    },
}
</script>



// WEBPACK FOOTER //
// FreeTerm.vue?3b3b2d69